import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import toast from 'react-hot-toast';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

export default function LoginPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const { login } = useAuthStore();

  // Check for session timeout message
  useEffect(() => {
    const sessionTimeout = localStorage.getItem('session_timeout');
    if (sessionTimeout === 'true') {
      toast.error('Oturum süreniz doldu. Güvenlik nedeniyle oturumunuz sonlandırıldı. Lütfen tekrar giriş yapın.', {
        duration: 6000,
        icon: '⏰',
      });
      // Clear the flag
      localStorage.removeItem('session_timeout');
    }
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'E-posta adresi gereklidir';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Geçerli bir e-posta adresi giriniz';
    }

    if (!formData.password) {
      newErrors.password = 'Şifre gereklidir';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Şifre en az 6 karakter olmalıdır';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      await login(formData.email, formData.password);
      toast.success('Başarıyla giriş yapıldı!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      
      const backendMsg = error.response?.data?.error || error.response?.data?.message;
      if (error.response?.status === 401) {
        toast.error('E-posta veya şifre hatalı');
      } else if (error.response?.status === 403) {
        // Nöbet planı yok / hesap aktif değil gibi özel mesajları göster
        toast.error(backendMsg || 'Hesabınız aktif değil. Lütfen yöneticinizle iletişime geçin.');
      } else {
        toast.error(backendMsg || 'Giriş sırasında bir hata oluştu. Lütfen tekrar deneyin.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      <div>
        <Input
          label="E-posta Adresi"
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          value={formData.email}
          onChange={handleChange}
          error={errors.email}
          placeholder="ornek@hastane.gov.tr"
          className="appearance-none rounded-md relative block w-full"
        />
      </div>

      <div className="relative">
        <Input
          label="Şifre"
          id="password"
          name="password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          required
          value={formData.password}
          onChange={handleChange}
          error={errors.password}
          placeholder="••••••••"
          className="appearance-none rounded-md relative block w-full pr-10"
        />
        <button
          type="button"
          className="absolute inset-y-0 right-0 top-6 pr-3 flex items-center"
          onClick={() => setShowPassword(!showPassword)}
        >
          {showPassword ? (
            <EyeSlashIcon className="h-5 w-5 text-gray-400" />
          ) : (
            <EyeIcon className="h-5 w-5 text-gray-400" />
          )}
        </button>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <input
            id="remember-me"
            name="remember-me"
            type="checkbox"
            className="h-4 w-4 text-medical-600 focus:ring-medical-500 border-gray-300 rounded"
          />
          <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
            Beni hatırla
          </label>
        </div>
      </div>

      <div>
        <Button
          type="submit"
          variant="medical"
          size="lg"
          className="group relative w-full flex justify-center"
          loading={isLoading}
          disabled={isLoading}
        >
          Giriş Yap
        </Button>
      </div>
    </form>
  );
} 